import {useLoader} from '@react-three/fiber'
import {isDebugMode} from '@repo/shared/utils/isDebugMode'
import {useAtom} from 'jotai'
import {useEffect, useMemo, useRef} from 'react'
import {Vector3} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader'
import {Player} from '../Player/Player'
import {activeSlotsState} from '../Store/atoms'
import FrameStore from '../Store/FrameStore'
import {getSceneReferencePoints} from './World.utils'

const fStore = new FrameStore()

export default function World() {
  const [activeSlots, setActiveSlots] = useAtom(activeSlotsState)

  const isDebug = useRef(isDebugMode())

  const gltf = useLoader(GLTFLoader, '/models/arena/arena_feb11.glb')

  /**
   * Get all the position refs from the gltf scene for the players
   */
  const refrencePoints = useMemo(() => getSceneReferencePoints(gltf.scene), [gltf.scene])

  /**
   * Set the path start, end and length
   */
  useEffect(() => {
    const startPos = refrencePoints['ref_point_team_1_start'] ?? new Vector3(0, 0, 0)
    const endPos = refrencePoints['ref_point_team_1_end'] ?? new Vector3(0, 0, 0)

    if (startPos && endPos) {
      fStore.setPathInfo(startPos, endPos)
    }
  }, [refrencePoints])

  /**
   * Check for teams and make sure players are added to the scene
   */
  const teamCheckIntervalTimer = useRef<NodeJS.Timeout | null>()

  const intervalHandler = () => {
    const leaderboard = fStore.getLeaderboard()

    if (leaderboard?.leaderboardTeams) {
      for (const team of leaderboard.leaderboardTeams) {
        if (team.slot === 1 && !activeSlots.slot1) {
          setActiveSlots({...activeSlots, slot1: team.team})
        }
        if (team.slot === 2 && !activeSlots.slot2) {
          setActiveSlots({...activeSlots, slot2: team.team})
        }
        if (team.slot === 3 && !activeSlots.slot3) {
          setActiveSlots({...activeSlots, slot3: team.team})
        }
        if (team.slot === 4 && !activeSlots.slot4) {
          setActiveSlots({...activeSlots, slot4: team.team})
        }
        if (team.slot === 5 && !activeSlots.slot5) {
          setActiveSlots({...activeSlots, slot5: team.team})
        }
      }
    } else {
      setActiveSlots({slot1: null, slot2: null, slot3: null, slot4: null, slot5: null})
    }
  }

  useEffect(() => {
    teamCheckIntervalTimer.current = setInterval(intervalHandler, 1000)

    return () => {
      clearInterval(teamCheckIntervalTimer.current!)
    }
  }, [activeSlots])

  if (!gltf.scene) return null

  return (
    <>
      <primitive object={gltf.scene} />

      {isDebug.current && (
        <>
          <mesh
            position={[
              fStore.getPathEndVec().x,
              fStore.getPathEndVec().y,
              fStore.getPathEndVec().z,
            ]}
          >
            <boxGeometry attach="geometry" args={[0.4, 12, 1]} />
            <meshStandardMaterial attach="material" color="blue" />
          </mesh>

          <mesh
            position={[
              fStore.getPathEndOffsetVec().x,
              fStore.getPathEndOffsetVec().y,
              fStore.getPathEndOffsetVec().z,
            ]}
          >
            <boxGeometry attach="geometry" args={[0.4, 6, 1]} />
            <meshStandardMaterial attach="material" color="red" />
          </mesh>
        </>
      )}

      {activeSlots.slot1 && (
        <Player refrencePoints={refrencePoints} slot={1} teamName={activeSlots.slot1} />
      )}
      {activeSlots.slot2 && (
        <Player refrencePoints={refrencePoints} slot={2} teamName={activeSlots.slot2} />
      )}
      {activeSlots.slot3 && (
        <Player refrencePoints={refrencePoints} slot={3} teamName={activeSlots.slot3} />
      )}
      {activeSlots.slot4 && (
        <Player refrencePoints={refrencePoints} slot={4} teamName={activeSlots.slot4} />
      )}
      {activeSlots.slot5 && (
        <Player refrencePoints={refrencePoints} slot={5} teamName={activeSlots.slot5} />
      )}
    </>
  )
}
